import { ConfigProvider } from 'aui';
// import en_US from 'aui/lib/locale/en_US';
import type { ClientConfigServerType } from '@/services/client-config/clientConfig';
import type { UrlConfigServerType } from '@/services/url-config/urlConfig';
import type { ServerEnvConfigType } from '@/shared/config/ServerEnvConfigType';
import type { SupportedLocale } from '@/shared/i18n/SupportedLocale';
import { UIKitConfigContext } from '@/ui-kit/components/UIKitConfigContext/UIKitConfigContext';
import React from 'react';
import {
  appearanceConfigInitialState,
  AppearanceConfigModelStoreProvider,
} from '../appearance-config/appearanceConfig';
import { ClientConfigModelStoreProvider } from '../client-config/clientConfig';
import type { ClientI18nState } from '../client-i18n/clientI18n';
import { ClientI18nModelStoreProvider } from '../client-i18n/clientI18n';
import type { DocsDataState } from '../docs-data/docsData';
import { DocsDataModelStoreProvider } from '../docs-data/docsData';
import { EnvConfigModelStoreProvider } from '../env-config/envConfig';
import {
  operationConfigInitialState,
  OperationConfigModelStoreProvider,
} from '../operation-config/operationConfig';
import { UrlConfigModelStoreProvider } from '../url-config/urlConfig';

export type GlobalModelProviderProps = {
  docsDataState: DocsDataState | undefined;
  i18nState: ClientI18nState | undefined;
  clientConfig: ClientConfigServerType | undefined;
  urlConfig: UrlConfigServerType | undefined;
  envConfig: ServerEnvConfigType | undefined;
};

export function GlobalModelProvider(props: React.PropsWithChildren<GlobalModelProviderProps>) {
  const { docsDataState, i18nState, clientConfig, urlConfig, envConfig, children } = props;

  const formConfig = React.useMemo(() => {
    return {
      requiredMark: true,
      colon: false,
    };
  }, []);

  const uiKitConfigContextValue = React.useMemo(() => {
    return {
      locale: i18nState?.clientLocale ?? DEFAULT_LOCALE,
      appName: envConfig?.APP_NAME ?? APP_NAME,
      appNameLower: envConfig?.APP_NAME_LOWER ?? String(APP_NAME).toLowerCase(),
    };
  }, [i18nState?.clientLocale, envConfig?.APP_NAME, envConfig?.APP_NAME_LOWER]);

  const newI18nState = React.useMemo(() => {
    return {
      clientLocale: i18nState?.clientLocale ?? (DEFAULT_LOCALE as SupportedLocale),
    };
  }, [i18nState?.clientLocale]);

  return (
    <ConfigProvider
      form={formConfig}
      prefixCls="ui"
      iconPrefixCls="appicon"
      // locale={en_US}
    >
      <UIKitConfigContext.Provider value={uiKitConfigContextValue}>
        <OperationConfigModelStoreProvider state={operationConfigInitialState}>
          <AppearanceConfigModelStoreProvider state={appearanceConfigInitialState}>
            <ClientI18nModelStoreProvider state={newI18nState}>
              {envConfig && clientConfig && urlConfig && docsDataState ? (
                <EnvConfigModelStoreProvider state={envConfig}>
                  <ClientConfigModelStoreProvider state={clientConfig}>
                    <UrlConfigModelStoreProvider state={urlConfig}>
                      <DocsDataModelStoreProvider state={docsDataState}>
                        {children}
                      </DocsDataModelStoreProvider>
                    </UrlConfigModelStoreProvider>
                  </ClientConfigModelStoreProvider>
                </EnvConfigModelStoreProvider>
              ) : (
                children
              )}
            </ClientI18nModelStoreProvider>
          </AppearanceConfigModelStoreProvider>
        </OperationConfigModelStoreProvider>
      </UIKitConfigContext.Provider>
    </ConfigProvider>
  );
}
